<template>
  <div class="coins-form">
    <v-card class="section-card" elevation="0">

      <!-- ═══ Card Header ═══ -->
      <div class="card-head">
        <div class="head-gradient head-gradient--red" />
        <div class="head-inner">

          <!-- Row: ชื่อ + ปุ่ม -->
          <div class="d-flex align-center justify-space-between">
            <div>
              <h2 class="form-title">
                {{ activeTab === 0 ? 'Coins ของฉัน' : 'Cash ของฉัน' }}
              </h2>
              <div class="subtitle">
                {{ activeTab === 0
                  ? 'ยอดคงเหลือและประวัติการใช้งาน EL-SON Coins'
                  : 'ยอดคงเหลือและประวัติการใช้งาน EL-SON Cash' }}
              </div>
            </div>
            <v-btn v-if="activeTab === 0" class="btn-convert-main" height="40" :disabled="balanceCoins < 5000" @click="convertOpen = true">
              <v-icon left small>mdi-swap-horizontal</v-icon>
              แลกเป็น Cash
            </v-btn>
            <div v-else class="d-flex align-center" style="gap:8px">
              <v-btn class="btn-withdraw-main" height="40" :disabled="balanceCash < 100" @click="withdrawOpen = true">
                <v-icon left small>mdi-bank-transfer-out</v-icon>
                ถอนเงิน
              </v-btn>
              <v-btn class="btn-topup-main" height="40" @click="openTopup">
                <v-icon left small>mdi-plus-circle-outline</v-icon>
                เติมเงิน
              </v-btn>
            </div>
          </div>

          <!-- Tabs แนบใต้ชื่อ -->
          <v-tabs v-model="activeTab" background-color="transparent" color="#d6230b" class="coins-tabs mt-3">
            <v-tab class="tab-item">
              <v-icon left small>mdi-currency-usd</v-icon>
              Coins
            </v-tab>
            <v-tab class="tab-item">
              <v-icon left small>mdi-cash-multiple</v-icon>
              Cash
            </v-tab>
          </v-tabs>

        </div>
      </div>

      <v-divider />

      <!-- ═══ Tab Content ═══ -->
      <v-tabs-items v-model="activeTab" style="background: transparent;">

        <!-- ── TAB 0: COINS ── -->
        <v-tab-item>
          <div class="section-form">

            <!-- ยอดคงเหลือ -->
            <div class="balance-row red-bg">
              <div class="coin-badge red-theme">
                <v-icon size="28" color="#d6230b">mdi-currency-usd</v-icon>
              </div>
              <div class="balance-main">
                <div class="balance-label">ยอดคงเหลือ EL-SON Coins</div>
                <div class="balance-amt red-text">{{ formatAmount(balanceCoins) }}</div>
                <div class="balance-exp">
                  <v-icon x-small color="#9ca3af" class="mr-1">mdi-clock-outline</v-icon>
                  หมดอายุ: {{ expireText }}
                </div>
              </div>
            </div>

            <v-divider class="my-5" />

            <!-- ประวัติ -->
            <div class="hist-head">
              <v-icon small color="#111827" class="mr-1">mdi-history</v-icon>
              ประวัติการรับ/ใช้ Coins
            </div>

            <!-- Loading -->
            <div v-if="loading" class="d-flex justify-center py-8">
              <v-progress-circular indeterminate size="32" width="3" color="#f59e0b" />
            </div>

            <!-- Empty -->
            <div v-else-if="items.length === 0" class="empty-wrap">
              <div class="empty-card">
                <v-icon size="56" color="#e5e7eb" class="mb-3">mdi-star-off-outline</v-icon>
                <div class="empty-title">ยังไม่มีประวัติ Coins</div>
                <div class="empty-sub">ซื้อสินค้าหรือร่วมกิจกรรมเพื่อรับ Coins เพิ่มเติม</div>
              </div>
            </div>

            <!-- History List -->
            <div v-else class="hist-list">
              <div
                v-for="it in items"
                :key="it.id"
                class="hist-item"
                style="cursor:pointer"
                @click="openChain(it)"
              >
                <div class="hist-left">
                  <div class="coin-badge sm" :class="it.amount >= 0 ? 'red-theme' : 'neg-theme'">
                    <v-icon size="20" :color="it.amount >= 0 ? '#d6230b' : '#ef4444'">
                      {{ it.amount >= 0 ? 'mdi-arrow-down-circle' : 'mdi-arrow-up-circle' }}
                    </v-icon>
                  </div>
                  <div class="hist-texts">
                    <div class="hist-title">{{ it.title }}</div>
                    <div class="hist-sub">{{ it.sub }}</div>
                    <div class="hist-date">{{ formatDate(it.at) }}</div>
                  </div>
                </div>
                <div class="hist-amt" :class="it.amount >= 0 ? 'pos-red' : 'neg'">
                  {{ it.amount >= 0 ? '+' : '' }}{{ formatAmount(it.amount) }}
                </div>
              </div>
            </div>

          </div>
        </v-tab-item>

        <!-- ── TAB 1: CASH ── -->
        <v-tab-item>
          <div class="section-form">
            <!-- ยอดคงเหลือ Cash -->
            <div class="balance-row red-bg">
              <div class="coin-badge red-theme">
                <v-icon size="28" color="#d6230b">mdi-cash-multiple</v-icon>
              </div>
              <div class="balance-main">
                <div class="balance-label">ยอดคงเหลือ EL-SON Cash</div>
                <div class="balance-amt red-text">{{ formatAmount(balanceCash) }}</div>
                <div class="balance-exp">
                  <v-icon x-small color="#9ca3af" class="mr-1">mdi-infinity</v-icon>
                  ไม่มีวันหมดอายุ
                </div>
              </div>
            </div>

            <v-divider class="my-5" />

            <div class="hist-head">
              <v-icon small color="#111827" class="mr-1">mdi-history</v-icon>
              ประวัติการรับ/ใช้ Cash
            </div>

            <!-- Empty Cash -->
            <div v-if="loading" class="d-flex justify-center py-8">
              <v-progress-circular indeterminate size="32" width="3" color="#d6230b" />
            </div>
            <div v-else-if="cashItems.length === 0" class="empty-wrap">
              <div class="empty-card">
                <v-icon size="56" color="#e5e7eb" class="mb-3">mdi-cash-off</v-icon>
                <div class="empty-title">ยังไม่มีประวัติการเติมเงิน</div>
                <div class="empty-sub">เริ่มเติมเงินเข้าระบบเพื่อใช้ชำระค่าสินค้า</div>
                <v-btn outlined color="#d6230b" class="mt-4 rounded-lg" @click="openTopup">
                  <v-icon left small>mdi-plus-circle-outline</v-icon>
                  เติม Cash แรก
                </v-btn>
              </div>
            </div>

            <!-- History List Cash -->
            <div v-else class="hist-list">
              <div v-for="it in cashItems" :key="it.id" class="hist-item">
                <div class="hist-left">
                  <div class="coin-badge sm" :class="it.status === 'rejected' ? 'neg-theme' : ''">
                    <v-icon size="20" :color="it.status === 'approved' ? '#10b981' : (it.status === 'pending' ? '#f59e0b' : '#ef4444')">
                      mdi-cash-clock
                    </v-icon>
                  </div>
                  <div class="hist-texts">
                    <div class="hist-title">{{ it.title }}</div>
                    <div class="hist-sub">
                      <v-chip x-small :color="getStatusColor(it.status)" class="white--text px-2" label>{{ getStatusText(it.status) }}</v-chip>
                    </div>
                    <div class="hist-date">{{ formatDate(it.at) }}</div>
                  </div>
                </div>
                <div class="hist-amt" :class="it.transactionType === 'deposit' ? 'pos' : 'neg'">
                  {{ it.transactionType === 'deposit' ? '+' : '-' }}{{ formatAmount(it.amount) }}
                </div>
              </div>
            </div>

          </div>
        </v-tab-item>

      </v-tabs-items>
    </v-card>

    <commission-chain-modal
      v-model="chainModal"
      :transaction="selectedTx"
      role="user"
    />

    <withdraw-dialog
      v-model="withdrawOpen"
      :balance="balanceCash"
      role="user"
      @done="fetchWallet"
    />

    <convert-points-dialog
      v-model="convertOpen"
      :balance="balanceCoins"
      role="user"
      @done="fetchWallet"
    />

    <!-- ═══ Dialog เติมเงิน Cash (QR + Slip) ═══ -->
    <v-dialog v-model="topupOpen" max-width="520" persistent>
      <v-card class="topup-card" elevation="0" v-if="!topupStep2">
        <div class="topup-head">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="#d6230b" class="mr-2">mdi-cash-plus</v-icon>
              <h3>เติมเงินเข้า EL-SON Cash</h3>
            </div>
            <v-btn icon @click="topupOpen = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
          <p class="muted">ระบุจำนวนเงินที่ต้องการเติม</p>
        </div>

        <v-divider class="mb-4" />

        <div class="topup-body">
          <span class="detail-text">จำนวนเงิน (บาท)</span>
          <v-text-field
            v-model.number="topupAmount"
            type="number" min="0" step="1" placeholder="เช่น 100"
            v-bind="tfProps" :rules="[req, min10]" @wheel.stop
          />

          <div class="quick-row">
            <v-chip v-for="n in quickAmounts" :key="n" class="quick-chip" @click="setQuick(n)" label>
              {{ n.toLocaleString('th-TH') }}
            </v-chip>
          </div>

          <div class="preview-row" v-if="Number(topupAmount) > 0">
            <v-icon small color="#d6230b" class="mr-1">mdi-cash-multiple</v-icon>
            จะได้รับ <b class="ml-1">{{ formatAmount(topupAmount) }}</b>
            <span class="ml-1">EL-SON Cash</span>
          </div>
        </div>

        <div class="topup-actions">
          <v-btn class="btn-outline" height="40" @click="topupOpen = false">ยกเลิก</v-btn>
          <v-btn class="btn-success-red" height="40"
            :disabled="!canTopup" 
            @click="gotoStep2">
            ต่อไป
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </v-card>

      <!-- Step 2: QR + Upload -->
      <v-card class="topup-card" elevation="0" v-else>
        <div class="topup-head">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="#d6230b" class="mr-2">mdi-qrcode-scan</v-icon>
              <h3>ชำระเงินและส่งหลักฐาน</h3>
            </div>
            <v-btn icon @click="topupOpen = false; topupStep2 = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
          <p class="muted">สแกน QR Code แล้วแนบสลิปเพื่อยืนยัน</p>
        </div>

        <v-divider class="mb-4" />

        <div class="topup-body text-center">
          <div class="text-subtitle-1 font-weight-bold mb-1">ยอดเงิน: ฿{{ formatAmount(topupAmount) }}</div>
          
          <!-- System Payment Info -->
          <v-card outlined class="pa-3 mb-4 text-left grey lighten-5 rounded-lg">
            <div class="d-flex align-center mb-1">
              <v-icon small color="primary" class="mr-2">mdi-account-box</v-icon>
              <span>{{ platformInfo.displayName || ((platformInfo.firstNameTh || '') + ' ' + (platformInfo.lastNameTh || '')).trim() || platformInfo.platformNameTh || 'บัญชีรับเงิน' }}</span>
            </div>
            <div class="d-flex align-center">
              <v-icon small color="primary" class="mr-2">mdi-numeric</v-icon>
              <span class="font-weight-bold text-h6 primary--text">{{ platformInfo.promptPayId }}</span>
              <v-btn icon x-small class="ml-2" @click="copyToClipboard(platformInfo.promptPayId)">
                <v-icon x-small>mdi-content-copy</v-icon>
              </v-btn>
            </div>
          </v-card>

          <div class="qr-container pa-4 mx-auto mb-4 qr-clickable" style="max-width: 250px; border: 2px dashed #d6230b; border-radius: 12px; position: relative; cursor: zoom-in;" @click="qrImageSrc && (zoomSrc = qrImageSrc, zoomDialog = true)">
            <div v-if="loadingQR" class="d-flex align-center justify-center" style="height: 200px;">
              <v-progress-circular indeterminate color="#d6230b"></v-progress-circular>
            </div>
            <template v-else>
              <v-img :src="qrImageSrc" contain />
              <v-btn fab x-small color="white" elevation="2"
                style="position:absolute; right:8px; bottom:36px;"
                @click.stop="zoomSrc = qrImageSrc; zoomDialog = true">
                <v-icon color="#d6230b">mdi-magnify-plus-outline</v-icon>
              </v-btn>
            </template>
            <div class="mt-2 caption grey--text">สแกนเพื่อโอนเงิน (กดที่รูปเพื่อขยาย)</div>
          </div>

          <v-dialog v-model="zoomDialog" max-width="640" @click:outside="zoomDialog = false">
            <v-card class="pa-0">
              <div class="d-flex justify-end pa-2">
                <v-btn icon @click="zoomDialog = false"><v-icon>mdi-close</v-icon></v-btn>
              </div>
              <div class="pa-4 pt-0 text-center" style="background:#fff;">
                <img :src="zoomSrc" alt="QR" style="max-width:100%; max-height:80vh; display:block; margin:0 auto;" />
              </div>
              <div class="text-center caption grey--text pb-3">กดพื้นที่ว่างนอกรูปเพื่อปิด</div>
            </v-card>
          </v-dialog>

          <v-divider class="my-4" />

          <div class="text-left mb-2">
            <span class="detail-text">แนบสลิปการโอนเงิน</span>
          </div>

          <div 
            v-if="!slipFile" 
            class="upload-box py-6 px-4 d-flex flex-column align-center justify-center"
            @click="$refs.slipInput.click()"
            style="border: 2px dashed #e2e8f0; border-radius: 12px; cursor: pointer; transition: 0.2s;"
          >
            <v-icon size="40" color="grey lighten-1">mdi-image-plus</v-icon>
            <div class="mt-2 text-body-2 grey--text">คลิกเพื่ออัปโหลดสลิป</div>
          </div>
          <div v-else class="preview-box">
            <v-img :src="slipPreview" max-height="250" contain class="rounded-lg border grey lighten-4" />
            <div class="preview-overlay" @click="$refs.slipInput.click()">
              <v-icon color="white">mdi-camera-retake</v-icon>
              <span class="white--text text-caption ml-1">เปลี่ยนรูป</span>
            </div>
            <v-btn fab x-small color="error" class="remove-slip-btn" @click.stop="removeSlip">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
          <input type="file" ref="slipInput" class="d-none" accept="image/*" @change="onSlipSelected" />
        </div>

        <v-divider class="my-2" />

        <div class="topup-actions">
          <v-btn class="btn-outline" height="40" @click="topupStep2 = false">
            <v-icon left small>mdi-arrow-left</v-icon>
            ย้อนกลับ
          </v-btn>
          <v-btn class="btn-success-red" height="40"
            :disabled="!slipFile || topupLoading" :loading="topupLoading"
            @click="submitTopupRequest">
            ยืนยันการเติมเงิน
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import swalAlert from "@/services/alert/swalServices";
import WalletService from "@/services/api/wallet";
import CommissionChainModal from "@/components/CommissionChainModal.vue";
import WithdrawDialog from "@/components/Shared/WithdrawDialog.vue";
import ConvertPointsDialog from "@/components/Shared/ConvertPointsDialog.vue";

export default {
  name: "MyCoins",
  components: { CommissionChainModal, WithdrawDialog, ConvertPointsDialog },
  data() {
    return {
      activeTab: 1,
      chainModal: false,
      selectedTx: null,
      withdrawOpen: false,
      convertOpen: false,
      balanceCoins: 0,
      balanceCash: 0,
      expireText: "ไม่มีวันหมดอายุ",
      items: [],
      cashItems: [],
      loading: false,

      // topup dialog
      topupOpen: false,
      topupStep2: false,
      topupAmount: null,
      topupLoading: false,
      quickAmounts: [50, 100, 200, 500, 1000],
      slipFile: null,
      slipPreview: null,

      zoomDialog: false,
      zoomSrc: "",

      tfProps: { outlined: true, hideDetails: "auto", dense: true },
      req: (v) => !!v || "กรุณากรอกจำนวนเงิน",
      min10: (v) => Number(v) >= 1 || "ขั้นต่ำ 1 บาท",

      platformInfo: {
        platformNameTh: "",
        promptPayId: "",
        bankName: "",
        qrCodeImage: ""
      },
      loadingQR: false,
      qrCodeUrl: ""
    };
  },
  computed: {
    canTopup() {
      return Number(this.topupAmount) >= 1;
    },
    qrImageSrc() {
      return (
        this.qrCodeUrl ||
        this.platformInfo.qrCodeImage ||
        ("https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=" + (this.platformInfo.promptPayId || "ELSON-TOPUP"))
      );
    },
  },
  watch: {
    topupOpen(val) {
      if (!val) {
        this.qrCodeUrl = "";
      }
    }
  },
  mounted() {
    this.fetchWallet();
    this.fetchPlatformInfo();
  },
  methods: {
    async fetchWallet() {
      this.loading = true;
      try {
        const res = await WalletService.getMyWallet();
        if (res?.data) {
          const data = res.data;
          // Cash use topUp.balance, Coins use commission.balance
          this.balanceCoins = data.commission?.balance || 0;
          this.balanceCash = data.topUp?.balance || 0;

          // Transactions for Coins (from CommissionWallet)
          this.items = (data.commission?.transactions || [])
            .map((t) => ({
              id: t.id,
              title: t.description || (t.transactionType === "commission_earned" ? "รับ Coins" : "ใช้ Coins"),
              sub: "สำเร็จ",
              at: t.createdAt,
              amount: t.amount,
            }));

          // Transactions for Cash (Topup history)
          this.cashItems = (data.topUp?.transactions || [])
            .map((t) => ({
              id: t.id,
              amount: t.amount,
              status: t.status,
              at: t.createdAt,
              transactionType: t.transactionType,
              title: t.description || (t.transactionType === "deposit" ? "เติมเงินเข้า Cash" : "ชำระเงินคืน/ถอนจาก Cash"),
              description: t.description
            }));
        }
      } catch (e) {
        console.error("fetchWallet Error:", e);
      } finally {
        this.loading = false;
      }
    },
    openChain(it) {
      this.selectedTx = it;
      this.chainModal = true;
    },
    formatAmount(n) {
      return Number(n || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(iso) {
      const d = new Date(iso);
      return `${d.toLocaleDateString("th-TH", { day: "2-digit", month: "2-digit", year: "numeric" })} ${d.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })}`;
    },
    getStatusText(s) {
      if (s === 'pending') return 'รอตรวจสอบ';
      if (s === 'approved') return 'สำเร็จ';
      if (s === 'rejected') return 'ปฏิเสธ';
      return s;
    },
    getStatusColor(s) {
      if (s === 'pending') return '#f59e0b';
      if (s === 'approved') return '#10b981';
      if (s === 'rejected') return '#ef4444';
      return 'grey';
    },
    openTopup() {
      this.topupAmount = null;
      this.slipFile = null;
      this.slipPreview = null;
      this.topupStep2 = false;
      this.topupOpen = true;
    },
    setQuick(n) {
      this.topupAmount = n;
    },
    onSlipSelected(e) {
      const file = e.target.files[0];
      if (file) {
        this.slipFile = file;
        this.slipPreview = URL.createObjectURL(file);
      }
    },
    removeSlip() {
      this.slipFile = null;
      this.slipPreview = null;
      if (this.$refs.slipInput) this.$refs.slipInput.value = "";
    },
    async fetchPlatformInfo() {
      try {
        const res = await WalletService.getPlatformPaymentInfo();
        if (res.data && res.data.data) {
          this.platformInfo = res.data.data;
        }
      } catch (e) {
        console.error("fetchPlatformInfo error:", e);
      }
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        swalAlert.Success("คัดลอกแล้ว", text);
      } catch (err) {
        console.error("Failed to copy: ", err);
      }
    },
    async fetchTopUpQRCode() {
      if (!this.topupAmount) return;
      this.loadingQR = true;
      try {
        const res = await WalletService.getTopUpQRCode(this.topupAmount);
        if (res.data && res.data.data) {
          this.qrCodeUrl = res.data.data;
        }
      } catch (e) {
        console.error("fetchTopUpQRCode error:", e);
      } finally {
        this.loadingQR = false;
      }
    },
    gotoStep2() {
      this.topupStep2 = true;
      this.fetchTopUpQRCode();
    },
    async submitTopupRequest() {
      if (!this.slipFile || !this.canTopup) return;
      this.topupLoading = true;
      try {
        const formData = new FormData();
        formData.append("amount", this.topupAmount);
        formData.append("slip", this.slipFile);

        await WalletService.topupWithSlip(formData);
        
        await swalAlert.Success("ส่งหลักฐานสำเร็จ", "กรุณารอเจ้าหน้าที่ตรวจสอบยอดเงิน");
        this.topupOpen = false;
        this.topupStep2 = false;
        this.fetchWallet();
      } catch (e) {
        await swalAlert.Error("ส่งหลักฐานไม่สำเร็จ", e?.response?.data?.message || "");
      } finally {
        this.topupLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Card ── */
.section-card {
  background: #fff;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(2, 6, 23, 0.06);
}

.card-head {
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  overflow: hidden;
}

.head-gradient {
  height: 76px;
  opacity: 0.18;
}

.head-gradient--red {
  background: linear-gradient(100deg, #d6230b 0%, #eed7d5 100%);
}

/* head-inner อยู่ถัดจาก gradient bar (normal flow เหมือน Favorites) */
.head-inner {
  padding: 22px 24px 0;
}

.form-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 2px;
  font-family: "Noto Sans Thai", system-ui, Arial, sans-serif;
  line-height: 1.3;
}

.subtitle {
  color: #6b7280;
  font-size: 12px;
}

/* ── Tabs ── */
.coins-tabs {
  margin-left: -4px;
}

::v-deep .coins-tabs .v-tab {
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0;
  text-transform: none;
  min-width: 90px;
  padding: 0 12px;
  color: #64748b !important;
}

::v-deep .coins-tabs .v-tab--active {
  color: #d6230b !important;
}

::v-deep .coins-tabs .v-tabs-slider-wrapper {
  height: 3px;
}

/* ── Buttons ── */
.btn-topup-main {
  background: #d6230b !important;
  color: #fff !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  min-width: 110px;
}

.btn-convert-main {
  background: #fef3c7 !important;
  color: #92400e !important;
  border: 1.5px solid #fcd34d !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  min-width: 120px;
}

.btn-withdraw-main {
  background: #fff !important;
  color: #2563eb !important;
  border: 1.5px solid #93c5fd !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  min-width: 100px;
}

.btn-topup-cash {
  background: #eff6ff !important;
  color: #60a5fa !important;
  border-radius: 10px !important;
  text-transform: none !important;
  min-width: 110px;
}

/* ── Section form ── */
.section-form {
  padding: 22px 24px 28px;
}

/* ── Balance Row ── */
.balance-row {
  display: flex;
  align-items: center;
  gap: 18px;
  background: #fffbf5;
  border: 1px solid #fde68a;
  border-radius: 14px;
  padding: 18px 20px;
}

.coin-badge {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fff7ed;
  border: 2px solid #fcd34d;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.coin-badge.blue-theme {
  background: #eff6ff;
  border-color: #93c5fd;
}

.coin-badge.sm {
  width: 38px;
  height: 38px;
}

.coin-badge.red-theme {
  background: #fff1f2;
  border-color: #fca5a5;
}

.coin-badge.neg-theme {
  background: #f9fafb;
  border-color: #e2e8f0;
}

.balance-main {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.balance-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.balance-amt {
  font-size: 34px;
  font-weight: 800;
  color: #f59e0b;
  line-height: 1;
}

.balance-exp {
  color: #9ca3af;
  font-size: 12px;
  display: flex;
  align-items: center;
}

/* ── History ── */
.hist-head {
  font-weight: 700;
  font-size: 14px;
  color: #111827;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.hist-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.hist-item {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  background: #fff;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(2, 6, 23, 0.04);
  padding: 12px 16px;
  align-items: center;
  transition: box-shadow 0.15s;
}

.hist-item:hover {
  box-shadow: 0 4px 12px rgba(2, 6, 23, 0.08);
}

.hist-left {
  display: flex;
  gap: 12px;
  align-items: center;
}

.hist-texts {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.hist-title {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
}

.hist-sub {
  color: #6b7280;
  font-size: 12px;
}

.hist-date {
  color: #9ca3af;
  font-size: 11px;
  margin-top: 1px;
}

.hist-amt {
  font-weight: 800;
  font-size: 15px;
  min-width: 90px;
  text-align: right;
}

.hist-amt.pos-red {
  color: #d6230b;
}

.hist-amt.pos {
  color: #10b981;
}

.hist-amt.neg {
  color: #ef4444;
}

.balance-row.red-bg {
  background: #fff5f5;
  border-color: #fecaca;
}

.balance-amt.red-text {
  color: #d6230b;
}

.btn-success-red {
  background: #d6230b !important;
  color: #fff !important;
  font-weight: 700 !important;
  border-radius: 10px !important;
  text-transform: none !important;
}

.qr-container {
  background: #fff;
}

::v-deep .quick-chip {
  background: #ffecb3 !important;
  border: 1px solid #fcd34d !important;
  font-weight: 700 !important;
  color: #92400e !important;
  border-radius: 8px !important;
}

::v-deep .quick-row .quick-chip:nth-child(4),
::v-deep .quick-row .quick-chip:nth-child(5) {
  background: #eef2ff !important;
  border-color: #6366f1 !important;
  color: #3730a3 !important;
}

/* ── Empty State ── */
.empty-wrap {
  text-align: center;
  padding: 52px 0 40px;
}

.empty-card {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.empty-title {
  font-size: 17px;
  font-weight: 700;
  color: #374151;
  margin-top: 4px;
}

.empty-sub {
  color: #9ca3af;
  font-size: 13px;
  margin-top: 6px;
}

/* ── Topup Dialog ── */
.topup-card {
  background: #fff;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(2, 6, 23, 0.1);
}

.topup-head {
  padding: 20px 22px 0;
}

.topup-head h3 {
  margin: 0;
  font-weight: 700;
  color: #111827;
  font-size: 17px;
}

.topup-head .muted {
  color: #80796b;
  font-size: 13px;
  margin: 4px 0 10px;
}

.topup-body {
  padding: 12px 22px 10px;
}

.detail-text {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1f2a39;
  margin-bottom: 6px;
}

.quick-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.upload-box:hover {
  border-color: #d6230b !important;
  background: #fff5f5;
}

.preview-row {
  margin-top: 12px;
  padding: 10px 14px;
  background: #fff1f2;
  border: 1px solid #fca5a5;
  border-radius: 10px;
  font-size: 14px;
  color: #1f2a39;
  display: flex;
  align-items: center;
}

.preview-box {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 12px;
}

.preview-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0,0,0,0.5);
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
}

.preview-overlay:hover {
  background: rgba(0,0,0,0.7);
}

.remove-slip-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2) !important;
}

.topup-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 22px 20px;
}

.btn-success {
  background: #d6230b !important;
  color: #fff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  min-width: 140px;
}

.btn-outline {
  background: #fff !important;
  color: #1f2937 !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 10px !important;
  text-transform: none !important;
}

::v-deep .v-input--dense .v-input__slot {
  min-height: 44px !important;
  height: 44px !important;
  padding: 0 12px !important;
  border-radius: 10px !important;
  border-color: #e5e7eb !important;
}

::v-deep .v-text-field.v-input--dense input {
  height: 24px !important;
  line-height: 24px !important;
  padding: 0 !important;
  font-size: 14px !important;
}

@media (max-width: 600px) {
  .head-inner {
    padding: 14px 14px 0;
  }

  .section-form {
    padding: 14px 14px 20px;
  }

  .hist-item {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .hist-amt {
    text-align: left;
  }

  .head-inner > .d-flex:first-child {
    flex-wrap: wrap;
    gap: 10px;
  }

  .btn-convert-main,
  .btn-withdraw-main,
  .btn-topup-main {
    min-width: unset !important;
    font-size: 13px !important;
  }

  .balance-row {
    padding: 14px;
    gap: 12px;
  }

  .balance-amt {
    font-size: 26px;
  }
}
</style>
